import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { authenticatedFetch } from '../../../services/api/apiClient'
import useAuthz from '../../../hooks/useAuthz'
import { apiEndpoints } from '../../../consts'
import logger from '../../../utils/logger'
import { LoadingSpinner } from '../../atoms'
import { DrawerCard, DrawerBadge, DrawerEmptyState } from '../../DrawerComponents'
import ManualQAFormDialog from '../ManualQAFormDialog'
import styles from '../CallDrawerModern.module.scss'

const CRITERIA_CONFIG = {
  callerNameConfirmation: { label: 'Name Confirmed', icon: 'user' },
  opposingPartyPhonetics: { label: 'Party Phonetics', icon: 'spell-check' },
  phoneNumberVerification: { label: 'Phone Verified', icon: 'phone' },
  emailPhonetics: { label: 'Email Phonetics', icon: 'at' },
  companyNameConfirmation: { label: 'Company Name', icon: 'building' },
  callNotes5Ws: { label: 'Call Notes', icon: 'sticky-note' }
}

export const QAEvaluationCard = ({ selectedRow, isGenerating: parentIsGenerating }) => {
  const { isAdmin } = useAuthz()
  const [qaEvaluation, setQAEvaluation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [manualQADialogOpen, setManualQADialogOpen] = useState(false)
  const [existingManualQA, setExistingManualQA] = useState(null)
  const [expandedCriteria, setExpandedCriteria] = useState({})

  const isGeneratingFromParent = parentIsGenerating

  const getCallId = useCallback(() => {
    if (selectedRow?.id) return selectedRow.id
    if (selectedRow?.call_id) return selectedRow.call_id
    if (selectedRow?.[3]) return selectedRow[3]
    return null
  }, [selectedRow])

  const getCampaignName = useCallback(() => {
    return selectedRow?.campaign_name || selectedRow?.campaignName || selectedRow?.[5] || null
  }, [selectedRow])

  const fetchQAEvaluation = useCallback(async () => {
    const callId = getCallId()
    if (!callId) {
      setIsLoading(false)
      return
    }

    try {
      const response = await authenticatedFetch(apiEndpoints.qaByCall(callId), {
        method: 'GET'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.data?.evaluation) {
          setQAEvaluation(data.data.evaluation)
        }
      }
    } catch (error) {
      logger.error('Error fetching QA evaluation:', error)

      if (selectedRow?.qa_evaluation) {
        try {
          const evaluation = typeof selectedRow.qa_evaluation === 'string'
            ? JSON.parse(selectedRow.qa_evaluation)
            : selectedRow.qa_evaluation
          setQAEvaluation(evaluation)
        } catch (e) {
        }
      }
    }
    setIsLoading(false)
  }, [getCallId, selectedRow])

  const fetchManualQA = useCallback(async () => {
    const callId = getCallId()
    if (!callId) return

    try {
      const response = await authenticatedFetch(apiEndpoints.qaManualByCall(callId))
      if (!response.ok) {
        setExistingManualQA(null)
        return
      }
      const data = await response.json()
      if (data.data) {
        setExistingManualQA(data.data)
      } else {
        setExistingManualQA(null)
      }
    } catch (err) {
      logger.error('Error fetching manual QA:', err)
      setExistingManualQA(null)
    }
  }, [getCallId])

  /* eslint-disable react-hooks/set-state-in-effect -- intentional reset and fetch when selectedRow changes */
  useEffect(() => {
    setQAEvaluation(null)
    setExistingManualQA(null)
    setIsLoading(true)
    setExpandedCriteria({})
  }, [selectedRow?.id, selectedRow?.call_id])

  useEffect(() => {
    fetchQAEvaluation()
    fetchManualQA()
  }, [fetchQAEvaluation, fetchManualQA])
  /* eslint-enable react-hooks/set-state-in-effect */

  const effectiveCriteria = useMemo(() => {
    if (!qaEvaluation?.criteria) return {}

    const overrides = existingManualQA?.autoQAOverride || {}
    const hasOverrides = Object.keys(overrides).length > 0

    if (!hasOverrides) {
      return qaEvaluation.criteria
    }

    const effective = {}
    Object.entries(qaEvaluation.criteria).forEach(([key, criterion]) => {
      if (overrides[key]) {
        effective[key] = {
          ...criterion,
          result: overrides[key].newResult,
          overridden: true,
          originalResult: criterion.result,
          overrideReason: overrides[key].reason
        }
      } else {
        effective[key] = criterion
      }
    })

    return effective
  }, [qaEvaluation, existingManualQA])

  const effectiveGrade = useMemo(() => {
    const criteria = Object.values(effectiveCriteria)
    if (criteria.length === 0) return null

    const gradedResults = criteria
      .map(c => c.result)
      .filter(r => r === 'PASS' || r === 'FAIL')

    if (gradedResults.length === 0) return 'N/A'

    return gradedResults.every(r => r === 'PASS') ? 'PASS' : 'FAIL'
  }, [effectiveCriteria])

  if (!isAdmin) return null

  const getCounts = () => {
    const entries = Object.values(effectiveCriteria)
    const gradedEntries = entries.filter(c => c.result === 'PASS' || c.result === 'FAIL')
    const pass = gradedEntries.filter(c => c.result === 'PASS').length
    const fail = gradedEntries.filter(c => c.result === 'FAIL').length
    return { pass, fail, total: gradedEntries.length }
  }

  const counts = getCounts()
  const hasOverrides = existingManualQA?.autoQAOverride && Object.keys(existingManualQA.autoQAOverride).length > 0

  const showGenerating = isLoading || (isGeneratingFromParent && !qaEvaluation)

  const getGradeBadge = () => {
    if (showGenerating) {
      return (
        <DrawerBadge variant="info">
          <LoadingSpinner size={12} />
          <span>Generating...</span>
        </DrawerBadge>
      )
    }

    if (!effectiveGrade) return null

    const isPass = effectiveGrade === 'PASS'

    return (
      <DrawerBadge variant={isPass ? 'pass' : 'fail'}>
        <FontAwesomeIcon icon={isPass ? 'check-circle' : 'times-circle'} />
        {effectiveGrade}
        {hasOverrides && <span className={styles.qaOverrideBadge}>Override</span>}
      </DrawerBadge>
    )
  }

  return (
    <>
      <DrawerCard
        title="QA Evaluation"
        icon="clipboard-check"
        badge={getGradeBadge()}
        collapsible
        defaultExpanded={!!selectedRow?.qa_evaluation}
      >
        <Box className={styles.qaCardInner}>
          {showGenerating ? (
            <Box className={styles.loadingIndicator}>
              <LoadingSpinner size={20} message='Generating QA evaluation...' />
            </Box>
          ) : qaEvaluation ? (
            <Box className={styles.qaContent}>
              {/* Score Summary */}
              <Box className={styles.qaScoreSummary}>
                <Box className={styles.qaScoreItem}>
                  <span className={`${styles.qaScoreValue} ${styles.qaScoreValuePass}`}>{counts.pass}</span>
                  <span className={styles.qaScoreLabel}>Pass</span>
                </Box>
                <Box className={styles.qaScoreDivider} />
                <Box className={styles.qaScoreItem}>
                  <span className={`${styles.qaScoreValue} ${styles.qaScoreValueFail}`}>{counts.fail}</span>
                  <span className={styles.qaScoreLabel}>Fail</span>
                </Box>
                {hasOverrides && (
                  <>
                    <Box className={styles.qaScoreDivider} />
                    <Box className={styles.qaScoreItem}>
                      <span className={`${styles.qaScoreValue} ${styles.qaScoreValueWarning}`}>
                        {Object.keys(existingManualQA.autoQAOverride).length}
                      </span>
                      <span className={styles.qaScoreLabel}>Overridden</span>
                    </Box>
                  </>
                )}
              </Box>

              {/* Criteria List */}
              <Box className={styles.qaCriteriaList}>
                {Object.entries(effectiveCriteria).map(([key, criterion]) => {
                  const config = CRITERIA_CONFIG[key] || { label: key, icon: 'question' }
                  const isPass = criterion.result === 'PASS'
                  const isFail = criterion.result === 'FAIL'
                  const isOverridden = criterion.overridden
                  const isExpanded = expandedCriteria[key]

                  const aiReason = criterion.reason && typeof criterion.reason === 'string' ? criterion.reason.trim() : ''
                  const aiEvidence = criterion.evidence
                    ? (typeof criterion.evidence === 'string' ? criterion.evidence.trim() : JSON.stringify(criterion.evidence))
                    : ''
                  const overrideReason = criterion.overrideReason || ''
                  const hasDetails = aiReason || aiEvidence || isOverridden

                  return (
                    <Box key={key} className={styles.qaCriteriaWrapper}>
                      <Box
                        className={`
                          ${styles.qaCriteriaItem}
                          ${isPass ? styles.qaCriteriaPass : isFail ? styles.qaCriteriaFail : styles.qaCriteriaNA}
                          ${isOverridden ? styles.qaCriteriaOverridden : ''}
                          ${hasDetails ? styles.qaCriteriaClickable : ''}
                        `}
                        onClick={() => hasDetails && setExpandedCriteria(prev => ({ ...prev, [key]: !prev[key] }))}
                      >
                        <FontAwesomeIcon
                          icon={isPass ? 'check' : isFail ? 'times' : 'minus'}
                          className={styles.qaCriteriaIcon}
                        />
                        <span className={styles.qaCriteriaLabel}>
                          {config.label}
                          {isOverridden && (
                            <FontAwesomeIcon
                              icon="edit"
                              className={styles.qaCriteriaOverrideIcon}
                              title="Manually overridden"
                            />
                          )}
                        </span>
                        {hasDetails && (
                          <FontAwesomeIcon
                            icon="chevron-down"
                            className={`${styles.qaCriteriaChevron} ${isExpanded ? styles.qaCriteriaChevronExpanded : ''}`}
                          />
                        )}
                      </Box>
                      {isExpanded && hasDetails && (
                        <Box className={styles.qaCriteriaDetail}>
                          {/* Show override info first if overridden */}
                          {isOverridden && (
                            <Box className={styles.qaCriteriaOverrideInfo}>
                              <Typography className={styles.qaCriteriaOverrideLabel}>
                                <FontAwesomeIcon icon="edit" /> Manual Override
                              </Typography>
                              <Typography className={styles.qaCriteriaOverrideChange}>
                                Changed from <span className={criterion.originalResult === 'PASS' ? styles.textPass : styles.textFail}>
                                  {criterion.originalResult}
                                </span> to <span className={isPass ? styles.textPass : styles.textFail}>
                                  {criterion.result}
                                </span>
                              </Typography>
                              {overrideReason && (
                                <Typography className={styles.qaCriteriaOverrideReason}>
                                  <strong>Reason:</strong> {overrideReason}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {/* Show original AI reason/evidence */}
                          {(aiReason || aiEvidence) && (
                            <Box className={isOverridden ? styles.qaCriteriaAiInfo : ''}>
                              {isOverridden && (
                                <Typography className={styles.qaCriteriaAiLabel}>
                                  <FontAwesomeIcon icon="robot" /> Original AI Assessment
                                </Typography>
                              )}
                              {aiReason && (
                                <Typography className={styles.qaCriteriaReason}>
                                  <strong>Reason:</strong> {aiReason}
                                </Typography>
                              )}
                              {aiEvidence && (
                                <Typography className={styles.qaCriteriaEvidence}>
                                  <strong>Evidence:</strong> &ldquo;{aiEvidence}&rdquo;
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>

              {/* Action Button */}
              <button
                className={styles.qaActionButton}
                onClick={() => setManualQADialogOpen(true)}
              >
                <FontAwesomeIcon icon="edit" />
                {hasOverrides ? 'Edit Override' : 'Manual QA'}
              </button>
            </Box>
          ) : (
            <DrawerEmptyState
              icon="clipboard-check"
              text="No QA data available"
            />
          )}
        </Box>
      </DrawerCard>

      {/* Manual QA Dialog */}
      <ManualQAFormDialog
        open={manualQADialogOpen}
        onClose={() => setManualQADialogOpen(false)}
        callId={getCallId()}
        campaignName={getCampaignName()}
        existingQAEvaluation={qaEvaluation}
        existingManualQA={existingManualQA}
        onSuccess={() => {
          fetchManualQA()
          fetchQAEvaluation()
        }}
      />
    </>
  )
}

QAEvaluationCard.propTypes = {
  selectedRow: PropTypes.object,
  isGenerating: PropTypes.bool
}

export default QAEvaluationCard
